import { useMarkets } from '@/state/markets';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useXClone } from '@/state/xclone';
import { useLife } from '@/state/life';
import { useEmail } from '@/state/email';
import { useCalendar } from '@/state/calendar';
import { useMeta } from '@/state/meta';
import { checkAchievements } from './achievements';
import { processRandomEvents } from './randomEvents';
import { checkTrueDeath } from './death';
import { processArcs } from './arcs';
import { getTicker } from '@/content/tickers';
import { ALL_HANDLES } from '@/content/xcloneHandles';
import { priceAt, addImpulse } from './prices';
import { seedFor } from './clock';
import { prngFromSeed } from './prng';
import { sounds } from '@/audio/synth';
import { useCinematic } from '@/components/CinematicTakeover';

/**
 * Phase 1 tick loop. Runs on the main thread for now; will move to a Web Worker
 * in Phase 2+ per ENGINE.md.
 *
 * Phase 1 responsibilities:
 *   - Limit-order auto-fill (when price crosses limit)
 *   - Scripted Phase-1 storyline drips (to demonstrate the world-is-alive feel)
 *
 * Phase 2 will replace the hard-coded drips with the storyline engine reading
 * MDX content, scheduled-event dispatch, etc.
 */

let intervalId: number | undefined;
let sessionStart = 0;
const firedDrips = new Set<string>();

// Persistence helpers so storylines don't re-fire on reload within a single
// character (cleared on ascension by clearAllArcsForAscension + a fresh ID).
const ENGINE_PERSIST_KEY = 'nasdaq.one:engine-fired:v1';
function loadFiredFromStorage(): void {
  try {
    const raw = localStorage.getItem(ENGINE_PERSIST_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { drips?: string[]; thresholds?: string[]; calendar?: string[]; xclone?: string[]; events?: string[]; heat?: string[] };
    if (parsed.drips) for (const id of parsed.drips) firedDrips.add(id);
    if (parsed.thresholds) for (const id of parsed.thresholds) firedThresholds.add(id);
    if (parsed.calendar) for (const id of parsed.calendar) firedCalendarEvents.add(id);
    if (parsed.heat) for (const id of parsed.heat) firedHeatThresholds.add(id);
  } catch {}
}
function saveFiredToStorage(): void {
  try {
    localStorage.setItem(ENGINE_PERSIST_KEY, JSON.stringify({
      drips: [...firedDrips],
      thresholds: [...firedThresholds],
      calendar: [...firedCalendarEvents],
      heat: [...firedHeatThresholds],
    }));
  } catch {}
}
export function clearEnginePersist(): void {
  firedDrips.clear();
  firedThresholds.clear();
  firedCalendarEvents.clear();
  firedHeatThresholds.clear();
  try { localStorage.removeItem(ENGINE_PERSIST_KEY); } catch {}
}

interface ScriptedDrip {
  id: string;
  atMs: number;       // ms after session start
  threadId: string;
  fromNpc: string;
  text: string;
  notifyText?: string;
  impulse?: { ticker: string; logDelta: number; durationMs: number };
}

const PHASE1_DRIPS: ScriptedDrip[] = [
  {
    id: 'wife-followup',
    atMs: 15_000,
    threadId: 'wife',
    fromNpc: 'wife',
    text: 'hello?? did you see my msg',
    notifyText: 'wife: hello??',
  },
  {
    id: 'broker-tari-spicy',
    atMs: 45_000,
    threadId: 'broker',
    fromNpc: 'broker',
    text: "TARI ripping right now. don't blow up your account please. you have a wife.",
    notifyText: 'broker: TARI ripping right now',
    impulse: { ticker: 'TARI', logDelta: 0.04, durationMs: 90_000 },
  },
  {
    id: 'mom-foxnews',
    atMs: 90_000,
    threadId: 'mom',
    fromNpc: 'mom',
    text: 'sweetie pls call. i forwarded you a foxnews article about you. ❤️',
    notifyText: 'mom: i forwarded you something ❤️',
  },
  {
    id: 'wife-leaving',
    atMs: 150_000,
    threadId: 'wife',
    fromNpc: 'wife',
    text: "okay. i'm going to my mother's. don't @ me on twitter again",
    notifyText: 'wife: going to my mother\'s',
  },
  {
    id: 'broker-margin-warning',
    atMs: 240_000,
    threadId: 'broker',
    fromNpc: 'broker',
    text: 'reminder, your margin is fine. in case you forgot what margin is.',
  },
  {
    id: 'shil-pundit-trash',
    atMs: 300_000,
    threadId: 'broker',
    fromNpc: 'broker',
    text: 'CNBC pundit just called SHIL "structurally unviable on a podcast level." stock is taking it personally.',
    notifyText: 'CNBC trashes SHIL on air',
    impulse: { ticker: 'SHIL', logDelta: -0.05, durationMs: 75_000 },
  },
];

export function startTickLoop(): void {
  if (intervalId !== undefined) return;
  sessionStart = Date.now();
  loadFiredFromStorage();
  seedXCloneIfEmpty();
  intervalId = window.setInterval(tick, 250);
  // Persist fired sets every 10s so storylines don't re-fire on reload
  window.setInterval(saveFiredToStorage, 10_000);
}

/**
 * X-Clone ambient post scheduling.
 *
 * Old (broken) approach: emit one post every fixed N seconds — felt mechanical.
 * New approach: per UTC-minute, deterministically pick a post COUNT (with
 * occasional silent minutes and occasional bursts) and distribute posts at
 * random offsets within the minute. Result: organic-feeling timing that
 * ALL players still see at the same wall-clock moments (deterministic).
 */

interface PendingEmission {
  atMs: number;
  fireAtMs: number;     // exact scheduled time (used as the seed for content)
  key: string;
  category: 'xclone';
}

const pending: PendingEmission[] = [];
const firedKeys = new Set<string>();
let lastMaterializedMinute = -1;

function materializeMinute(minuteStartMs: number): void {
  // X-Clone post count: skewed distribution + time-of-day modifier.
  // Doomscrolling spikes at night (21:00–05:00 UTC), morning is quiet
  // (06:00–12:00 UTC), weekends bias toward chaos.
  const utcHour = new Date(minuteStartMs).getUTCHours();
  const utcDay = new Date(minuteStartMs).getUTCDay(); // 0 Sun, 6 Sat
  let densityMult = 1;
  if (utcHour >= 21 || utcHour < 5) densityMult = 1.4;       // night doomscroll
  else if (utcHour >= 6 && utcHour < 12) densityMult = 0.7;  // morning quiet
  if (utcDay === 0 || utcDay === 6) densityMult *= 1.15;     // weekend bias

  const countSeed = seedFor('xclone:density', minuteStartMs, 60_000);
  const cRng = prngFromSeed(countSeed);
  const r = cRng();
  let count: number;
  if (r < 0.07) count = 0;                       // 7% silent minutes
  else if (r < 0.20) count = 1 + Math.floor(cRng() * 2);  // 13% slow (1-2)
  else if (r < 0.85) count = 3 + Math.floor(cRng() * 5);  // 65% normal (3-7)
  else count = 8 + Math.floor(cRng() * 8);       // 15% bursty (8-15)
  count = Math.round(count * densityMult);

  // Distribute timestamps within the minute (deterministic random offsets)
  const offsetSeed = seedFor('xclone:offsets', minuteStartMs, 60_000);
  const oRng = prngFromSeed(offsetSeed);
  for (let i = 0; i < count; i++) {
    const offset = Math.floor(oRng() * 60_000);
    const fireAtMs = minuteStartMs + offset;
    pending.push({
      atMs: fireAtMs,
      fireAtMs,
      key: `xc:${fireAtMs}:${i}`,
      category: 'xclone',
    });
  }
}

function emitAmbientPostAtTime(fireAtMs: number): void {
  // Seed content from the exact scheduled time so all clients agree
  const seed = seedFor('xclone:content', fireAtMs, 1);
  const rng = prngFromSeed(seed);
  const handle = ALL_HANDLES[Math.floor(rng() * ALL_HANDLES.length)];
  if (!handle) return;
  const post = handle.posts[Math.floor(rng() * handle.posts.length)];
  if (!post) return;
  const likes = Math.floor(rng() * 4500 + 30);
  const reposts = Math.floor(rng() * 700 + 2);
  useXClone.getState().receivePost({
    authorHandle: handle.handle,
    authorTribe: handle.tribe,
    text: post,
    likes,
    reposts,
  });
}

function flushPending(now: number): void {
  for (let i = pending.length - 1; i >= 0; i--) {
    const p = pending[i]!;
    if (p.atMs > now) continue;
    if (!firedKeys.has(p.key)) {
      firedKeys.add(p.key);
      if (p.category === 'xclone') emitAmbientPostAtTime(p.fireAtMs);
    }
    pending.splice(i, 1);
  }
  // Cap firedKeys size to avoid unbounded growth
  if (firedKeys.size > 5000) {
    const toDelete = firedKeys.size - 4000;
    let n = 0;
    for (const k of firedKeys) {
      if (n++ >= toDelete) break;
      firedKeys.delete(k);
    }
  }
}

function seedXCloneIfEmpty(): void {
  if (useXClone.getState().posts.length > 0) return;
  // Materialize the previous 4 minutes worth so the feed isn't empty on first load.
  const now = Date.now();
  const currentMinute = Math.floor(now / 60_000);
  for (let i = 4; i >= 1; i--) {
    const minuteStart = (currentMinute - i) * 60_000;
    const countSeed = seedFor('xclone:density', minuteStart, 60_000);
    const cRng = prngFromSeed(countSeed);
    const r = cRng();
    let count: number;
    if (r < 0.07) count = 0;
    else if (r < 0.20) count = 1 + Math.floor(cRng() * 2);
    else if (r < 0.85) count = 3 + Math.floor(cRng() * 5);
    else count = 8 + Math.floor(cRng() * 8);
    const offsetSeed = seedFor('xclone:offsets', minuteStart, 60_000);
    const oRng = prngFromSeed(offsetSeed);
    const offsets: number[] = [];
    for (let j = 0; j < count; j++) offsets.push(Math.floor(oRng() * 60_000));
    offsets.sort((a, b) => a - b);
    // emit chronologically so feed order is sensible
    for (const offset of offsets) {
      emitAmbientPostAtTime(minuteStart + offset);
    }
  }
}

export function stopTickLoop(): void {
  if (intervalId !== undefined) {
    window.clearInterval(intervalId);
    intervalId = undefined;
  }
}

function tick(): void {
  const m = useMarkets.getState();
  const now = Date.now();

  // Conditional-order auto-fill (limit + stop)
  for (const order of m.openOrders) {
    const cfg = getTicker(order.ticker);
    if (!cfg) continue;
    const livePrice = priceAt(cfg, now);

    if (order.type === 'limit' && order.limitPrice !== undefined) {
      const buyHit = order.side === 'buy' && livePrice <= order.limitPrice;
      const sellHit = order.side === 'sell' && livePrice >= order.limitPrice;
      if (buyHit || sellHit) {
        m.applyFill(order.ticker, order.side, order.qty, order.limitPrice);
        m.cancelOrder(order.id);
        sounds.fill();
      }
    } else if (order.type === 'stop' && order.stopPrice !== undefined) {
      // Stop-loss (sell at/below trigger) or stop-buy/breakout (buy at/above trigger)
      const sellHit = order.side === 'sell' && livePrice <= order.stopPrice;
      const buyHit = order.side === 'buy' && livePrice >= order.stopPrice;
      if (sellHit || buyHit) {
        m.applyFill(order.ticker, order.side, order.qty, livePrice);
        m.cancelOrder(order.id);
        sounds.fill();
      }
    } else if (order.type === 'trail' && order.trailPct !== undefined) {
      const peak = order.trailPeak ?? livePrice;
      if (order.side === 'sell') {
        // Sell trail: ratchet peak UP, trigger when price drops trailPct below peak
        const newPeak = Math.max(peak, livePrice);
        if (newPeak > peak * 1.0005) m.updateTrailPeak(order.id, newPeak);
        const triggerPrice = newPeak * (1 - order.trailPct);
        if (livePrice <= triggerPrice) {
          m.applyFill(order.ticker, 'sell', order.qty, livePrice);
          m.cancelOrder(order.id);
          sounds.fill();
        }
      } else {
        // Buy trail: ratchet peak DOWN, trigger when price rises trailPct above trough
        const newPeak = Math.min(peak, livePrice);
        if (newPeak < peak * 0.9995) m.updateTrailPeak(order.id, newPeak);
        const triggerPrice = newPeak * (1 + order.trailPct);
        if (livePrice >= triggerPrice) {
          m.applyFill(order.ticker, 'buy', order.qty, livePrice);
          m.cancelOrder(order.id);
          sounds.fill();
        }
      }
    }
  }

  // Equity ATH ratchet — total liquid + market value of positions. New peaks
  // ping the player and feed the wallet drawdown indicator.
  let posValue = 0;
  for (const p of m.positions) {
    const cfg = getTicker(p.ticker);
    if (cfg) posValue += p.qty * priceAt(cfg, now);
  }
  const equity = m.cash + posValue;
  if (equity > m.equityATH && equity > 5_000) {
    if (m.bumpEquityATH(equity)) {
      // Soft chime only when crossing meaningful round thresholds (10K, 50K,
      // 100K, 500K, 1M, …) — otherwise every tiny tick spams the player.
      const milestones = [10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 5_000_000, 10_000_000, 50_000_000, 100_000_000, 1_000_000_000];
      const prev = m.equityATH;
      const crossed = milestones.find((ms) => prev < ms && equity >= ms);
      if (crossed) sounds.bigWin();
    }
  }

  // Scripted Phase-1 storyline drips
  const elapsed = now - sessionStart;
  for (const drip of PHASE1_DRIPS) {
    if (firedDrips.has(drip.id)) continue;
    if (elapsed < drip.atMs) continue;
    firedDrips.add(drip.id);
    usePhone.getState().receiveMessage({
      threadId: drip.threadId,
      fromNpc: drip.fromNpc,
      text: drip.text,
    });
    useNotifications.getState().push({
      surfaceId: 'phone',
      severity: 'info',
      text: drip.notifyText ?? `${drip.fromNpc}: ${drip.text.slice(0, 50)}`,
    });
    sounds.smsReceived();
    if (drip.impulse) {
      addImpulse({
        ticker: drip.impulse.ticker,
        startMs: now,
        durationMs: drip.impulse.durationMs,
        logDelta: drip.impulse.logDelta,
      });
    }
  }

  // X-Clone ambient: materialize each new UTC minute, then flush due emissions.
  // Schedule is deterministic from the world clock, but timing within each
  // minute is irregular (sometimes silent, sometimes bursty).
  const currentMinute = Math.floor(now / 60_000);
  if (lastMaterializedMinute < currentMinute) {
    // Materialize current minute and the next one too so events fire on time
    if (lastMaterializedMinute < 0) lastMaterializedMinute = currentMinute - 1;
    for (let m = lastMaterializedMinute + 1; m <= currentMinute + 1; m++) {
      materializeMinute(m * 60_000);
    }
    lastMaterializedMinute = currentMinute + 1;
  }
  flushPending(now);

  // Life decay
  useLife.getState().applyDecayUpTo(now);

  // Lawyer retainer drain — $1,400 / hour while active
  processLawyerRetainer(now);

  // Stat-threshold storyline drips (Life pillar in motion)
  processLifeThresholds(now);

  // Player trade reactions
  processTradeReactions(now);

  // Calendar event impulses
  processCalendarEvents(now);

  // Random world events (deterministic per-minute bucket)
  processRandomEvents(now);

  // Heat threshold watchdog — fires single notifications when crossing for the
  // first time. Tracked in module state so re-crossings after cooldown don't spam.
  watchHeatThresholds();

  // Multi-step authored arcs (wife divorce, IRS audit, going viral)
  processArcs();

  // Achievements
  checkAchievements();

  // True death (vanishingly rare)
  checkTrueDeath();
}

const firedThresholds = new Set<string>();

const firedHeatThresholds = new Set<string>();
function watchHeatThresholds(): void {
  const life = useLife.getState();
  // Once-per-character heat triggers. Reset implicitly via clearEnginePersist
  // on ascension. No throttle — these only fire on *first* crossing.
  if (life.secHeat >= 50 && !firedHeatThresholds.has('sec-50')) {
    firedHeatThresholds.add('sec-50');
    usePhone.getState().receiveMessage({
      threadId: 'broker', fromNpc: 'broker',
      text: "we need to talk about your account.\n\nin person. not over text. not over phone. compliance is requesting it. that\'s a thing they do not request lightly.\n\n— Compliance",
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'broker: compliance wants a meeting' });
    sounds.notifyAlert();
  }
  if (life.secHeat >= 75 && !firedHeatThresholds.has('sec-75')) {
    firedHeatThresholds.add('sec-75');
    useEmail.getState().receive({
      fromNpc: 'irs',
      fromAddress: 'noreply@sec.gov',
      subject: 'Notice of Investigation — your account',
      body: "Per your trading activity, the Securities and Exchange Commission has opened a formal inquiry.\n\nYou are required to preserve all records relating to the following tickers: [REDACTED]\n\nFailure to preserve constitutes spoliation, which is a separate offense.\n\nDo not, under any circumstances, post about this on social media.\n\n— SEC, Division of Enforcement",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'SEC sent a formal notice — preserve everything' });
    sounds.notifyAlert();
  }
  if (life.irsHeat >= 50 && !firedHeatThresholds.has('irs-50')) {
    firedHeatThresholds.add('irs-50');
    useEmail.getState().receive({
      fromNpc: 'irs',
      fromAddress: 'audit-flag@irs.gov',
      subject: 'Notice CP-2000 — Discrepancy',
      body: "Per your filing, the Service has identified discrepancies relating to capital gains reporting.\n\nYou have 30 days to respond. Failure results in escalation. Escalation cannot be reversed.\n\nThis notice is automated. The escalation will not be.\n\n— Internal Revenue Service",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'urgent', text: 'IRS notice CP-2000 — 30-day response window' });
    sounds.notifyAlert();
  }
  if (life.fbiHeat >= 60 && !firedHeatThresholds.has('fbi-60')) {
    firedHeatThresholds.add('fbi-60');
    usePhone.getState().receiveMessage({
      threadId: 'lawyer', fromNpc: 'lawyer',
      text: "i was just visited at the office. by two people. with credentials.\n\nthey did not ask for you. they asked AROUND you. that\'s worse.\n\nbillable.\n\n— Lou",
    });
    useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'lawyer: FBI was at his office' });
    sounds.notifyAlert();
  }
}

let lastLawyerTickMs = 0;
function processLawyerRetainer(now: number): void {
  const life = useLife.getState();
  if (life.lawyerRetainerActiveSince === null) {
    lastLawyerTickMs = now;
    return;
  }
  if (lastLawyerTickMs === 0) lastLawyerTickMs = now;
  const elapsedMs = now - lastLawyerTickMs;
  if (elapsedMs <= 0) return;
  // $1,400/hour = $1,400 / 3,600,000 ms = $0.000389 / ms
  const drain = (1400 / 3_600_000) * elapsedMs;
  useMarkets.getState().addCash(-drain);
  lastLawyerTickMs = now;
}

function processLifeThresholds(_now: number): void {
  const life = useLife.getState();

  fireOnce('wife-cold-1', () => life.relationships.wife <= 10, () => {
    usePhone.getState().receiveMessage({
      threadId: 'wife', fromNpc: 'wife',
      text: "are you even reading these",
    });
    notifyPhone('wife: are you even reading these');
  });

  fireOnce('wife-cold-2', () => life.relationships.wife <= -10, () => {
    usePhone.getState().receiveMessage({
      threadId: 'wife', fromNpc: 'wife',
      text: "i know you're 'making moves'. so am i. mine involves a lawyer.",
    });
    notifyPhone('wife: mine involves a lawyer.', 'important');
  });

  fireOnce('mom-worry-1', () => life.relationships.mom <= 30, () => {
    usePhone.getState().receiveMessage({
      threadId: 'mom', fromNpc: 'mom',
      text: "sweetie. did you eat. dad says hi. he's worried too.",
    });
    notifyPhone('mom: did you eat?');
  });

  fireOnce('sanity-low-1', () => life.sanity <= 30, () => {
    useXClone.getState().composePost("ok hot take: maybe the markets actually cant be wrong and im the one whos wrong");
    notify('x-clone', 'You posted something at 3am.', 'info');
  });

  fireOnce('irs-heat-25', () => life.irsHeat >= 25, () => {
    useEmail.getState().receive({
      fromNpc: 'irs',
      fromAddress: 'no-reply@irs.gov',
      subject: 'Friendly Reminder Regarding Your Recent Activity',
      body: "We noticed some unusual patterns in your reported gains.\n\nThis is not an audit notice.\n\n— Internal Revenue Service",
    });
    notify('email', 'IRS: friendly reminder regarding your recent activity.', 'important');
  });

  fireOnce('irs-heat-50', () => life.irsHeat >= 50, () => {
    useEmail.getState().receive({
      fromNpc: 'irs',
      fromAddress: 'audit-services@irs.gov',
      subject: 'NOTICE OF AUDIT — Tax Year 2026',
      body: 'You are hereby notified that your tax return for the period ending Dec 31, 2026 has been selected for review.\n\nPlease produce all records.\n\nRefusal will be construed as the position taken.\n\n— IRS Audit Services',
    });
    notify('email', 'IRS: NOTICE OF AUDIT', 'urgent');
    sounds.marginCall();
    useCinematic.getState().fire({
      id: 'cin-irs-audit',
      type: 'breaking',
      headline: 'IRS HAS QUESTIONS',
      subhead: 'A trader, name redacted, just hit Notice of Audit territory.',
    });
  });
}

const lastSeenFillIds = new Set<string>();

function processTradeReactions(_now: number): void {
  // React when a new fill happens — pick a random WSB or fintwit handle to dunk/cheer
  const fills = useMarkets.getState().recentFills;
  if (fills.length === 0) return;
  const latest = fills[0]!;
  const key = `${latest.at}:${latest.ticker}:${latest.qty}`;
  if (lastSeenFillIds.has(key)) return;
  lastSeenFillIds.add(key);
  if (lastSeenFillIds.size > 100) {
    // simple cap; oldest entries are arbitrary, fine for MVP
    const first = lastSeenFillIds.values().next().value;
    if (first) lastSeenFillIds.delete(first);
  }

  // 60% chance to react
  const seed = seedFor(`trade-react:${latest.ticker}:${latest.at}`, 0, 1);
  const rng = prngFromSeed(seed);
  if (rng() > 0.6) return;

  const tribe: ('wsb' | 'fintwit') = rng() > 0.5 ? 'wsb' : 'fintwit';
  const dunkPool = tribe === 'wsb' ? WSB_DUNKS : FINTWIT_DUNKS;
  const text = (dunkPool[Math.floor(rng() * dunkPool.length)] ?? '').replace('{ticker}', latest.ticker);
  if (!text) return;
  const handle = ALL_HANDLES.find((h) => h.tribe === tribe);
  useXClone.getState().receivePost({
    authorHandle: handle?.handle ?? 'anon',
    authorTribe: tribe,
    text,
    likes: Math.floor(rng() * 3000 + 50),
    reposts: Math.floor(rng() * 400 + 5),
  });
}

const WSB_DUNKS = [
  'this guy just bought {ticker}. respect the chaos.',
  'GUH he held {ticker} for like 8 seconds',
  'imagine clicking buy on {ticker}. couldnt be me.',
  "somebody just yolo'd {ticker}. send help.",
  'this man is going to be on a documentary in 3 years. {ticker}.',
];

const FINTWIT_DUNKS = [
  'unusual options activity in {ticker}. by which i mean: someone just clicked buy.',
  '{ticker} gettin some retail flow. don\'t be the bag.',
  'tape: {ticker} just printed retail. fade incoming.',
  'spotted: discretionary trader. doing exactly what you\'d expect. {ticker}.',
  'genuinely impressive {ticker} entry. wrong direction, but committed.',
];

const firedCalendarEvents = new Set<string>();

function processCalendarEvents(now: number): void {
  const events = useCalendar.getState().events;
  for (const e of events) {
    if (firedCalendarEvents.has(e.id)) continue;
    if (now < e.startTimeMs) continue;
    if (now > e.startTimeMs + Math.max(60_000, e.durationMin * 60_000)) {
      // missed it (offline gap) — silently mark fired so we don't fire stale events
      firedCalendarEvents.add(e.id);
      continue;
    }
    firedCalendarEvents.add(e.id);
    fireCalendarEvent(e.id);
  }
}

interface CalendarEffect {
  impulses?: { ticker: string; logDelta: number; durationMs: number }[];
  cinematic?: { headline: string; subhead: string };
  notification?: { text: string; severity: 'info' | 'important' | 'urgent' };
  emails?: { fromNpc: string; fromAddress: string; subject: string; body: string }[];
  xPosts?: { authorHandle: string; authorTribe?: 'wsb' | 'fintwit' | 'cnbc' | 'maga' | 'left' | 'crypto' | 'press' | 'mom'; text: string }[];
}

function fireCalendarEvent(id: string): void {
  const seed = seedFor(`calendar-effect:${id}`, 0, 1);
  const rng = prngFromSeed(seed);
  const hawkish = rng() > 0.5;

  const effect = ((): CalendarEffect => {
    if (id.startsWith('cpi-')) {
      const hot = rng() > 0.5;
      return {
        impulses: [
          { ticker: 'TARI', logDelta: hot ? -0.012 : 0.008, durationMs: 4 * 60_000 },
          { ticker: 'BOOM', logDelta: hot ? -0.005 : 0.004, durationMs: 4 * 60_000 },
          { ticker: 'CHIP', logDelta: hot ? -0.018 : 0.012, durationMs: 4 * 60_000 },
        ],
        cinematic: {
          headline: hot ? 'CPI HOTTER THAN EXPECTED' : 'CPI COOLER THAN EXPECTED',
          subhead: hot ? 'rate-cut hopes evaporated within 6 seconds' : 'JPow allowed to keep his job another month',
        },
        notification: { text: hot ? 'CPI hot. things going down.' : 'CPI cool. things going up.', severity: 'important' },
      };
    }
    if (id.startsWith('fomc-')) {
      return {
        impulses: [
          { ticker: 'BOOM', logDelta: hawkish ? -0.01 : 0.012, durationMs: 8 * 60_000 },
          { ticker: 'CHIP', logDelta: hawkish ? -0.022 : 0.025, durationMs: 8 * 60_000 },
          { ticker: 'OAEN', logDelta: hawkish ? -0.018 : 0.018, durationMs: 8 * 60_000 },
        ],
        cinematic: {
          headline: hawkish ? 'FED HOLDS. JPOW LOOKED ANGRY.' : 'FED CUTS 25BPS. JPOW LOOKED TIRED.',
          subhead: hawkish ? 'tech is taking it personally' : 'risk on. for now.',
        },
        notification: { text: hawkish ? 'FOMC: hawkish.' : 'FOMC: cut.', severity: 'urgent' },
      };
    }
    if (id.startsWith('earnings-tari')) {
      const beat = rng() > 0.4;
      return {
        impulses: [{ ticker: 'TARI', logDelta: beat ? 0.06 : -0.05, durationMs: 6 * 60_000 }],
        cinematic: {
          headline: beat ? 'TARI BEATS — STOCK GOES BRRRRRR' : 'TARI MISSES — CFO RESIGNED MID-CALL',
          subhead: beat ? 'analysts upgrading. CEO doing a victory tweet.' : 'analysts downgrading. CEO blaming "the deep state".',
        },
        notification: { text: beat ? '$TARI beats earnings.' : '$TARI misses earnings.', severity: 'important' },
      };
    }
    if (id.startsWith('earnings-boom')) {
      return {
        impulses: [{ ticker: 'BOOM', logDelta: 0.04, durationMs: 6 * 60_000 }],
        notification: { text: '$BOOM earnings call: more conflicts, more revenue.', severity: 'info' },
      };
    }
    if (id.startsWith('rally-')) {
      return {
        impulses: [
          { ticker: 'TARI', logDelta: 0.018, durationMs: 30 * 60_000 },
          { ticker: 'BOOM', logDelta: 0.012, durationMs: 30 * 60_000 },
        ],
        notification: { text: 'rally going off. $TARI calls getting frisky.', severity: 'info' },
      };
    }
    if (id === 'wife-bday') {
      return {
        notification: { text: 'wife\'s birthday TODAY. you remembered.', severity: 'urgent' },
      };
    }
    if (id === 'wife-anniv') {
      return {
        notification: { text: 'wedding anniversary today.', severity: 'urgent' },
      };
    }
    if (id === 'mom-bday') {
      return {
        notification: { text: "mom's birthday today. she will text you first. text her first.", severity: 'important' },
      };
    }
    if (id === 'sister-wedding') {
      return {
        notification: { text: "sister's wedding TODAY. uncle barry will be at table 4.", severity: 'urgent' },
        emails: [{
          fromNpc: 'sister',
          fromAddress: 'amelia.s@gmail.com',
          subject: 'TODAY',
          body: "TODAY.\n\nthat\'s the email. that\'s the whole email.\n\n— amelia",
        }],
      };
    }
    if (id.startsWith('earnings-pope')) {
      const beat = rng() > 0.4;
      return {
        impulses: [{ ticker: 'POPE', logDelta: beat ? 0.05 : -0.04, durationMs: 6 * 60_000 }],
        notification: { text: beat ? '$POPE beats. divine.' : '$POPE misses. ungodly.', severity: 'info' },
      };
    }
    if (id.startsWith('earnings-wall')) {
      const beat = rng() > 0.5;
      return {
        impulses: [{ ticker: 'WALL', logDelta: beat ? 0.06 : -0.07, durationMs: 6 * 60_000 }],
        notification: { text: beat ? '$WALL beats.' : '$WALL misses.', severity: 'info' },
      };
    }
    if (id.startsWith('earnings-poll')) {
      const beat = rng() > 0.5;
      return {
        impulses: [{ ticker: 'POLL', logDelta: beat ? 0.04 : -0.05, durationMs: 6 * 60_000 }],
        notification: { text: beat ? '$POLL beats.' : '$POLL misses.', severity: 'info' },
      };
    }
    if (id.startsWith('earnings-pray')) {
      const beat = rng() > 0.4;
      return {
        impulses: [{ ticker: 'PRAY', logDelta: beat ? 0.045 : -0.04, durationMs: 6 * 60_000 }],
        notification: { text: beat ? '$PRAY beats.' : '$PRAY misses.', severity: 'info' },
      };
    }
    if (id.startsWith('earnings-grft')) {
      const beat = rng() > 0.6;
      return {
        impulses: [{ ticker: 'GRFT', logDelta: beat ? 0.05 : -0.06, durationMs: 6 * 60_000 }],
        notification: { text: beat ? '$GRFT beats.' : '$GRFT misses.', severity: 'important' },
      };
    }
    if (id.startsWith('earnings-chip')) {
      const beat = rng() > 0.5;
      return {
        impulses: [
          { ticker: 'CHIP', logDelta: beat ? 0.07 : -0.08, durationMs: 8 * 60_000 },
          { ticker: 'OAEN', logDelta: beat ? 0.04 : -0.05, durationMs: 8 * 60_000 },
        ],
        notification: { text: beat ? '$CHIP beats. semis ripping.' : '$CHIP misses. semis tanking.', severity: 'important' },
      };
    }
    if (id.startsWith('earnings-musk')) {
      return {
        impulses: [{ ticker: 'MUSK', logDelta: hawkish ? -0.06 : 0.08, durationMs: 8 * 60_000 }],
        notification: { text: 'MUSK earnings: CEO tweeted through it. tape moved on the tweets.', severity: 'important' },
      };
    }
    if (id.startsWith('earnings-')) {
      // Generic earnings handler for anything else
      const beat = rng() > 0.5;
      return {
        notification: { text: beat ? 'earnings beat. tape green.' : 'earnings miss. tape red.', severity: 'info' },
      };
    }
    if (id === 'macro-jobless' || id === 'nfp-next' || id === 'macro-pmi' || id === 'macro-retail' || id === 'macro-gdp') {
      return {
        impulses: [
          { ticker: 'BOOM', logDelta: hawkish ? -0.008 : 0.006, durationMs: 4 * 60_000 },
          { ticker: 'TARI', logDelta: hawkish ? -0.006 : 0.005, durationMs: 4 * 60_000 },
        ],
        notification: { text: hawkish ? 'macro print: ugly. tape down.' : 'macro print: fine. tape up.', severity: 'info' },
      };
    }
    if (id.startsWith('court-')) {
      return {
        notification: { text: `court hearing today: ${id.replace('court-', '$').toUpperCase()}. verdict TBD.`, severity: 'important' },
      };
    }
    return {};
  })();

  if (effect.impulses) {
    for (const imp of effect.impulses) {
      addImpulse({ ticker: imp.ticker, startMs: Date.now(), durationMs: imp.durationMs, logDelta: imp.logDelta });
    }
  }
  if (effect.cinematic) {
    useCinematic.getState().fire({
      id: `cal-${id}`,
      type: 'breaking',
      headline: effect.cinematic.headline,
      subhead: effect.cinematic.subhead,
      durationMs: 5000,
    });
  }
  if (effect.notification) {
    useNotifications.getState().push({
      surfaceId: 'tv',
      severity: effect.notification.severity,
      text: effect.notification.text,
    });
    if (effect.notification.severity === 'urgent') sounds.notifyAlert();
    else sounds.notifyInfo();
  }
}

function notify(surfaceId: 'phone' | 'x-clone' | 'email', text: string, severity: 'info' | 'important' | 'urgent' | 'passive' = 'info'): void {
  useNotifications.getState().push({ surfaceId, severity, text });
  if (severity === 'urgent') sounds.notifyAlert();
  else if (severity === 'important') sounds.notifyAlert();
  else sounds.smsReceived();
}

function notifyPhone(text: string, severity: 'info' | 'important' | 'urgent' = 'info'): void {
  notify('phone', text, severity);
}

const LORE_TITLES: Record<string, string> = {
  'wife-cold-1': 'Wife — first cold message',
  'wife-cold-2': 'Wife — lawyer threat',
  'mom-worry-1': 'Mom — first concerned text',
  'sanity-low-1': 'You — first 3am post',
  'irs-heat-25': 'IRS — friendly reminder',
  'irs-heat-50': 'IRS — Notice of Audit',
};

function fireOnce(id: string, condition: () => boolean, action: () => void): void {
  if (firedThresholds.has(id)) return;
  if (!condition()) return;
  firedThresholds.add(id);
  action();
  const title = LORE_TITLES[id] ?? id;
  useMeta.getState().recordLore(id, title);
}
